import React, { useMemo, useState } from 'react';

import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Alert, Box, CircularProgress, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';

import { useGetChatConversationStats } from '~/hooks/apis/trip-planner/useGetChatConversationStats';

import { DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS, TRIP_PLANNER_CHAT_STATS_TIMEZONE } from './tripPlannerChatStatsUtils';

const DAILY_CONVERSATIONS_COLOR = '#1565c0';
const TRIPS_PER_USER_COLOR = '#2e7d32';

type Props = {
  chatId?: string;
  userId?: string;
};

export default function TripPlannerChatStats({ chatId, userId }: Props) {
  const [includeLuxuryEscapesUsers, setIncludeLuxuryEscapesUsers] = useState(false);
  const { data, isLoading, error } = useGetChatConversationStats({
    chatId,
    userId,
    days: DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS,
    includeLuxuryEscapesUsers,
  });

  const filtersSummary = useMemo(() => {
    const filters = [chatId ? `Conversation ID: ${chatId}` : null, userId ? `User ID: ${userId}` : null].filter(
      Boolean,
    );

    const baseSummary = `Last ${DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS} days. ${TRIP_PLANNER_CHAT_STATS_TIMEZONE} time.`;

    return filters.length > 0 ? `${baseSummary} Filtered by ${filters.join(', ')}` : baseSummary;
  }, [chatId, userId]);

  const summaryStats = useMemo(
    () => [
      { label: 'Today', value: data?.summary.today ?? 0 },
      { label: 'Last 7 Days', value: data?.summary.last7Days ?? 0 },
      { label: 'Last 30 Days', value: data?.summary.last30Days ?? 0 },
      { label: 'Avg / Day', value: data?.summary.averagePerDay ?? 0 },
    ],
    [data],
  );

  const totalUsersWithTrips = useMemo(
    () => data?.userDistributionChartData.reduce((sum, point) => sum + point.users, 0) ?? 0,
    [data],
  );

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6">Chat Stats</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtersSummary}
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeLuxuryEscapesUsers}
                  onChange={(_event, checked) => setIncludeLuxuryEscapesUsers(checked)}
                />
              }
              label="Include @luxuryescapes.com users"
            />
          </Box>
        </Stack>

        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load chat stats. Please refresh the page and try again.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              {summaryStats.map((stat) => (
                <Paper key={stat.label} variant="outlined" sx={{ p: 2, minWidth: 120 }}>
                  <Typography variant="overline" color="text.secondary">
                    {stat.label}
                  </Typography>
                  <Typography variant="h5">{stat.value}</Typography>
                </Paper>
              ))}
            </Stack>

            {data && data.summary.last30Days === 0 ? (
              <Alert severity="info">No chat conversations were created in the last 30 days.</Alert>
            ) : (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Conversations Per Day
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <LineChart data={data?.chartData ?? []} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tickMargin={8} />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          formatter={(value: number) => [value, 'Conversations']}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ''}
                        />
                        <Line
                          type="monotone"
                          dataKey="conversations"
                          stroke={DAILY_CONVERSATIONS_COLOR}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Conversations Per User
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Users with at least one conversation in this window, grouped into conversation-count ranges.
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={data?.userDistributionChartData ?? []}
                        margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tickMargin={8} />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          formatter={(value: number) => {
                            const percentage =
                              totalUsersWithTrips > 0 ? ((value / totalUsersWithTrips) * 100).toFixed(1) : '0.0';

                            return [`${value} (${percentage}%)`, 'Users'];
                          }}
                        />
                        <Bar dataKey="users" fill={TRIPS_PER_USER_COLOR} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
